import React from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { GameProvider } from './contexts/GameContext';
import { GameSpaceProvider } from './contexts/GameSpaceContext';
import { MsalProvider } from './auth/MsalProvider';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Layout/ProtectedRoute';
import GameRoute from './components/Layout/GameRoute';
import GameSpaceRoute from './components/Layout/GameSpaceRoute';

// Page Components
import Home from './pages/Home';
import CreateGame from './pages/CreateGame';
import GameLobby from './pages/GameLobby';
import GamePlay from './pages/GamePlay';
import GameResults from './pages/GameResults';
import ProManagement from './pages/ProManagement';
import TokensPage from './pages/TokensPage';
import GameSessionsPage from './pages/GameSessionsPage';
import GameSpaceList from './components/GameSpace/GameSpaceList';
import GameSpaceLobby from './components/GameSpace/GameSpaceLobby';

// Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css';

// Custom CSS for additional styling
const customStyles = `
  .min-vh-100 {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  main {
    flex: 1;
  }

  .answer-option {
    transition: all 0.2s ease;
    border: 2px solid transparent;
  }

  .answer-option:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }

  .answer-selected {
    border-color: var(--bs-primary) !important;
    box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
  }

  .answer-correct {
    border-color: var(--bs-success) !important;
    box-shadow: 0 0 0 0.2rem rgba(var(--bs-success-rgb), 0.25);
  }

  .answer-incorrect {
    border-color: var(--bs-danger) !important;
    box-shadow: 0 0 0 0.2rem rgba(var(--bs-danger-rgb), 0.25);
  }

  .question-text {
    line-height: 1.4;
    word-wrap: break-word;
  }

  .navbar-brand {
    font-weight: 700;
  }

  .card {
    transition: all 0.2s ease;
  }

  .card:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }

  .btn {
    transition: all 0.2s ease;
  }

  .spinner-border {
    width: 3rem;
    height: 3rem;
  }

  @media (max-width: 768px) {
    .h1 {
      font-size: 2rem;
    }
    
    .display-4 {
      font-size: 2.5rem;
    }
    
    .answer-option {
      min-height: 60px !important;
      font-size: 0.9rem;
    }
  }
`;

function App() {
  return (
    <>
      <style>{customStyles}</style>
      <MsalProvider>
        <AuthProvider>
          <GameProvider>
            <GameSpaceProvider>
              <Routes>
                <Route path="/" element={<Layout />}>
                  {/* Public Routes */}
                  <Route index element={<Home />} />
                  <Route 
                    path="login" 
                    element={<Navigate to="/" replace />}
                  />
                  {/* Protected Routes */}
                  <Route 
                    path="join" 
                    element={<Navigate to="/" replace />}
                  />
                  <Route 
                    path="create" 
                    element={
                      <ProtectedRoute>
                        <CreateGame />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="game/:gameCode" 
                    element={<Navigate to="/" replace />}
                  />
                  <Route 
                    path="pro" 
                    element={
                      <ProtectedRoute>
                        <ProManagement />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="tokens" 
                    element={
                      <ProtectedRoute>
                        <TokensPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="gamespaces" 
                    element={
                      <GameSpaceRoute requirePro={false}>
                        <GameSpaceList />
                      </GameSpaceRoute>
                    } 
                  />
                  <Route 
                    path="gamespace/:id" 
                    element={
                      <GameSpaceRoute requireSpecificGameSpace={true}>
                        <GameSpaceLobby />
                      </GameSpaceRoute>
                    } 
                  />
                  <Route 
                    path="gamespace/:gameSpaceId/sessions" 
                    element={
                      <GameSpaceRoute requireSpecificGameSpace={true}>
                        <GameSessionsPage />
                      </GameSpaceRoute>
                    } 
                  />
                  <Route 
                    path="gamespace" 
                    element={
                      <GameSpaceRoute>
                        <GameSpaceLobby />
                      </GameSpaceRoute>
                    } 
                  />
                  {/* Game Routes - Require being in a game */}
                  <Route 
                    path="lobby" 
                    element={
                      <GameRoute requireGameStatusName={['WaitingForPlayers']}>
                        <GameLobby />
                      </GameRoute>
                    } 
                  />
                  <Route 
                    path="play" 
                    element={
                      <GameRoute requireGameStatusName={['InProgress']}>
                        <GamePlay />
                      </GameRoute>
                    } 
                  />
                  <Route 
                    path="results" 
                    element={
                      <GameRoute requireGameStatusName={['Completed']}>
                        <GameResults />
                      </GameRoute>
                    } 
                  />
                  {/* 404 Fallback */}
                  <Route 
                    path="*" 
                    element={
                      <div className="container py-5 text-center">
                        <h1 className="display-1 text-muted">404</h1>
                        <h3>Page Not Found</h3>
                        <p className="text-muted">The page you're looking for doesn't exist.</p>
                        <a href="/" className="btn btn-primary">
                          <i className="fas fa-home me-2"></i>
                          Go Home
                        </a>
                      </div>
                    } 
                  />
                </Route>
              </Routes>
            </GameSpaceProvider>
          </GameProvider>
        </AuthProvider>
      </MsalProvider>
    </>
  );
}

export default App;
